import type { CSSProperties, Ref } from 'react'
import { useEffect } from 'react'
import { observable, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react-lite'

import type { Photos } from '@jotta/types/Photos'
import { useBrandStore } from '@jotta/ui/useBrandTheme'
import { cn, px } from '@jotta/utils/css'
import { groupByAspectRatio } from '@jotta/utils/groupByAspectRatio'
import {
  getFixedVirtualRowHeightPositions,
  getVisibleIndexes,
} from '@jotta/virtualized'

import type { CollectionRef } from '../../../api/useCollection'
import { usePhotoStore } from '../../../store/PhotoContext'

import { AlbumPhotoThumb } from './AlbumPhotoThumb'

const rowHeight = 225
const edgeFillThreshold = 0.5
const gapPx = 2
const overscanRows = 10
function touchesRightEdge(fillRatio?: number) {
  return (fillRatio ?? 1) >= edgeFillThreshold
}

function getRoundedClasses(opts: {
  isFirstRow: boolean
  isLastRow: boolean
  isFirstInRow: boolean
  isLastInRow: boolean
  allowTopRight: boolean
  allowBottomRight: boolean
}) {
  const {
    isFirstRow,
    isLastRow,
    isFirstInRow,
    isLastInRow,
    allowTopRight,
    allowBottomRight,
  } = opts

  return cn({
    'rounded-tl-[12px]': isFirstInRow && isFirstRow,
    'rounded-bl-[12px]': isFirstInRow && isLastRow,
    'rounded-tr-[12px]': isLastInRow && allowTopRight,
    'rounded-br-[12px]': isLastInRow && allowBottomRight,
  })
}

function getTileFlexStyle(
  alignStart: boolean,
  photo: Photos.Media,
): CSSProperties {
  if (!alignStart) {
    return { flex: '1 1 0%' }
  }

  const rawAspect =
    photo.width > 0 && photo.height > 0 ? photo.width / photo.height : 1
  const aspect = Number.isFinite(rawAspect) && rawAspect > 0 ? rawAspect : 1
  const basisPx = Math.max(1, Math.round(aspect * rowHeight))
  return { flex: `0 0 ${basisPx}px` }
}

export const AlbumPhotosLarge = observer(function AlbumPhotosLarge({
  ids,
  showOwnerAvatar,
  showSimilarSearch,
  collectionRef,
  containerRef,
}: {
  ids: readonly string[]
  showOwnerAvatar?: boolean
  showSimilarSearch?: boolean
  collectionRef: CollectionRef
  containerRef?: Ref<HTMLDivElement>
}) {
  const branding = useBrandStore()
  const photoStore = usePhotoStore()

  const store = useLocalObservable(
    () => ({
      ids,
      setIds(ids: readonly string[]) {
        this.ids = ids
      },
      get photos() {
        return photoStore.mediaObjects.getPhotosByIds(this.ids)
      },
      get rowAspect() {
        return branding.contentWidthRounded / rowHeight
      },
      get grouped() {
        return groupByAspectRatio<Photos.Media>(this.photos, this.rowAspect)
      },
      get positions() {
        return getFixedVirtualRowHeightPositions(
          this.grouped.rows.length,
          rowHeight,
        )
      },
      get visibleIndexes() {
        return getVisibleIndexes(
          rowHeight,
          branding.viewportHeight,
          branding.scrollTop,
          overscanRows,
        )
      },
      get visibleRows() {
        const rows = this.grouped.rows
        return this.positions.positions
          .slice(this.visibleIndexes.first, this.visibleIndexes.last)
          .map(p => ({ ...p, row: rows[p.index] }))
      },
    }),
    { ids: observable.ref },
  )

  useEffect(() => {
    runInAction(() => store.setIds(ids))
  }, [store, ids])

  const rows = store.grouped.rows
  const lastRowIndex = Math.max(rows.length - 1, 0)

  const viewRows = store.visibleRows.map(p => {
    const rowIndex = p.index
    const row = p.row

    const isFirstRow = rowIndex === 0
    const isLastRow = rowIndex === lastRowIndex

    const alignStart = row.fillRatio < edgeFillThreshold

    const nextRow = rows[rowIndex + 1]
    const nextRowAlignStart =
      Boolean(nextRow) && (nextRow.fillRatio ?? 1) < edgeFillThreshold

    const nextRowTouchesRight =
      Boolean(nextRow) &&
      !nextRowAlignStart &&
      touchesRightEdge(nextRow?.fillRatio)

    const tiles = row.items.map((photo, idx) => {
      const isFirstInRow = idx === 0
      const isLastInRow = idx === row.items.length - 1

      const allowTopRight = isFirstRow && isLastInRow
      const allowBottomRight =
        isLastInRow && (isLastRow || !nextRowTouchesRight)

      return {
        id: photo.id,
        photo,
        rounded: getRoundedClasses({
          isFirstRow,
          isLastRow,
          isFirstInRow,
          isLastInRow,
          allowTopRight,
          allowBottomRight,
        }),
      }
    })

    return {
      key: row.key,
      top: p.top,
      alignStart,
      tiles,
    }
  })

  const listStyle: CSSProperties = { height: px(store.positions.height) }
  const rowStyleBase: CSSProperties = {
    height: rowHeight,
    gap: gapPx,
    paddingBottom: gapPx,
  }

  return (
    <div className="pb-20" ref={containerRef}>
      <div className="relative w-full" style={listStyle}>
        {viewRows.map(row => (
          <div
            key={row.key}
            className={cn(
              'absolute flex w-full max-w-full overflow-visible',
              row.alignStart && 'justify-start',
            )}
            style={{ ...rowStyleBase, top: px(row.top) }}
            data-align-start={row.alignStart}
          >
            {row.tiles.map(t => (
              <div
                key={t.id}
                style={getTileFlexStyle(row.alignStart, t.photo)}
                className={cn(
                  'has-[:focus-visible]:z-10 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-0 has-[:focus-visible]:outline-m3-primary',
                  t.rounded,
                )}
              >
                <div className={cn('h-full w-full overflow-hidden', t.rounded)}>
                  <AlbumPhotoThumb
                    id={t.photo.id}
                    showOwnerAvatar={showOwnerAvatar}
                    showSimilarSearch={showSimilarSearch}
                    collectionRef={collectionRef}
                  />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
})
